import prepareAssertions from '~/packages/variable/prepareAssertions.util'
import { Variable } from '~/packages/variable/variables.typing'
import { defineAction, number, object, string } from '~/prix'
import eadEnrollmentStatsAction from './eadEnrollmentStats.action.json' assert { type: 'json' }
import { childrenType, spaceForPoint } from '~/prix/types/presets/space'
import { timeForIncidence } from '~/prix/types/presets/time'
import { select } from '~/prix/types/presets/select'
import { variable } from '~/prix/types/presets/variable'
import { assertions } from '~/prix/types/presets/assertions'
import { analysis } from '~/prix/types/presets/analysis'
import prefixProperties from '~/prix/utils/prefixProperties'
import enrollmentEad from '~/packages/legalEntityGeoprocessing/map/ead/enrollment.entity'
import expression from '~/prix/types/expression'
import studentEad from '~/packages/legalEntityGeoprocessing/map/ead/student.entity'
import getRecommendedCacheOptions from '~/utils/server/stats/getRecommendedCacheOptions'
import { defaultDetectionLayers } from '~/utils/server/stats/defaultTimeSeriesLayers'

const combinedShape = object({
  ...prefixProperties('matricula.', enrollmentEad.schema.shape),
  ...prefixProperties('aluno.', studentEad.schema.shape),
})

const eadStatsExpression = expression(combinedShape.shape)

export const eadEnrollmentVariable: Variable = {
  key: 'eadEnrollment',
  categoryKey: 'eadEnrollment',
  categoryKeys: ['eadEnrollment'],
  actionDefinitionKey: eadEnrollmentStatsAction.key,
  title: 'Todas as matrículas',
  label: 'Geral',
  unitSingular: 'matrícula',
  unitPlural: 'matrículas',
  grammaticalGender: 'female',
  timeType: 'incidence',
  shape: combinedShape,
  betterDensity: 'more',
  valueRules: {
    minValue: 0,
  },
  relevance: 0.3,
  payload: {
    productName: 'Matrículas no EAD',
    portfolioName: ['Cursos Online'],
  },
  basicSelect: ['matricula.dataMatricula', 'aluno.nome'],
  detailedSelect: [],
  timeSeries: {
    model: 'additive',
    fitDays: 365 * 10,
    layers: [
      ...defaultDetectionLayers,

      // Camadas de composição
      { layer: 'calendarSeasonality', type: 'year', degree: 1 },
      {
        layer: 'calendarSeasonality',
        type: 'dayOfWeek',
        degree: 1,
        optimization: {
          minimumDays: 60,
          lossFunction: 'recentWeightedMeanSquaredError',
        },
      },
      { layer: 'calendarSeasonality', type: 'monthOfYear', degree: 0 },
      { layer: 'trend', degree: 1 },
      { layer: 'calendarSeasonality', type: 'holiday', degree: 1 },
      { layer: 'calendarSeasonality', type: 'dayOfMonth', degree: 1 },
      { layer: 'trend', degree: 2 },
    ],
  },
  assertions: prepareAssertions(eadStatsExpression),
  compatibleChildrenTypes: ['country', 'macroRegion', 'state'],
}

const eadEnrollmentStatsDefinition = defineAction({
  ...eadEnrollmentStatsAction,
  input: object({
    variable,
    highlight: eadStatsExpression.isOptional(),
    filter: eadStatsExpression.isOptional(),
    space: spaceForPoint().isOptional(),
    time: timeForIncidence().isOptional(),
    select: select(combinedShape),
    assertions,
    analysis,
  }),
  output: object({
    items: object({
      value: number(),
      geoId: string().isOptional(),
      geoType: childrenType.isOptional(),
      dateTime: string().isOptional(),
    }),
  }),
  cache: getRecommendedCacheOptions,
})

export default eadEnrollmentStatsDefinition
